import React, { useRef, useState } from "react";
import { Form, Button, message, Typography, Tag, Input } from "antd";
import DietTable from "./DietTable";
import {
  afternoonKeys,
  customTag,
  eveningKeys,
  mergeDietData,
  midnightKeys,
  morningKeys,
  transformData,
} from "./nutritionistKeys";
import { addPatientDietItemsApi } from "../../../../services/nutritionist.services";
import btnStyles from "../../../../styles/Custom/Button.module.css";
import useQuery from "../../../../hooks/useQuery";
import DietEditTable from "./EditChart/DietEditTable";
import { getLabelTagsWithChecklistLables } from "../../../IpEMR/InspectionSummaries/constructTextFromValues";
import { otherSpecificationsOptions } from "../../../IpEMR/Options/dietAdviceOptions";
import { EyeOutlined } from "@ant-design/icons";
import DietHistoryDrawer from "./DietHistory/DietHistory";

const NurtritionistDietChart = ({ refresh, dietDetails, diet }) => {
  const params = useQuery();
  const [form] = Form.useForm();
  const formRef = useRef(null);
  const isActive = params?.status === "current";
  console.log(diet);
  const keyData = [
    ...midnightKeys,
    ...morningKeys,
    ...afternoonKeys,
    ...eveningKeys,
  ];

  const aggregateDietSlots = [
    diet?.slots?.mid_night || [],
    diet?.slots?.morning || [],
    diet?.slots?.afternoon || [],
    diet?.slots?.evening || [],
  ].flat();

  const mergedData = mergeDietData(keyData, aggregateDietSlots);
  // console.log(mergedData);

  const [dietData, setDietData] = useState(mergedData);

  // Handle changes to the diet table
  const handleTableChange = (updatedData) => {
    setDietData(updatedData);
  };

  // Submit the form data
  const onFinish = async (values) => {
    // console.log(values);
    const payload = {
      comments: values.comments || "",
      diet_items: transformData(dietData),
      additional_info: {
        diet_items: combinedTotals,
        nutritional_recommendations:
          dietDetails?.nutritional_recommendations ??
          dietDetails?.ia?.nutritional_recommendations,
        otherSpecifications:
          dietDetails?.otherSpecifications ??
          dietDetails?.ia?.otherSpecifications,
        dietType: dietDetails?.dietType ?? dietDetails?.ia?.dietType,
      },
    };
    // console.log(payload);
    addPatientDietItemsApi(params?.patient, payload)
      .then((res) => {
        message.success(res.data.message);
        refresh();
      })
      .catch((err) => message.error(err.response.data.message));
  };

  // Calculate totals for a given diet data
  const calculateDietTotals = (data) => {
    return data.reduce(
      (totals, item) => {
        totals.proteins += item.proteins || 0;
        totals.calories += item.calories || 0;
        totals.sodium += item.sodium || 0;
        totals.potassium += item.potassium || 0;
        totals.carbohydrate += item.carbohydrates || 0;
        return totals;
      },
      { calories: 0, proteins: 0, carbohydrate: 0, sodium: 0, potassium: 0 }
    );
  };

  const getUnits = (key) =>
    ["proteins", "carbohydrates"]?.includes(key)
      ? "g"
      : key === "calories"
      ? "kcal"
      : "mg";

  // Map diet data to custom tags
  const mapToCustomTag = (data, color) => {
    return Object.entries(data)?.map(([key, value]) =>
      customTag(
        key === "calories" ? "energy" : key,
        value,
        color,
        getUnits(key)
      )
    );
  };

  const totals = calculateDietTotals(dietData);

  const combinedTotals = {
    calories: totals.calories,
    proteins: totals.proteins,
    carbohydrates: totals.carbohydrate,
    sodium: totals.sodium,
    potassium: totals.potassium,
  };

  const filterOnDietType = (data) => {
    if ((dietDetails?.dietType ?? dietDetails?.ia?.dietType) === "Oral") {
      return Object.fromEntries(
        Object.entries(data).filter(([key]) =>
          ["proteins", "protein", "calories"].includes(key)
        )
      );
    }
    return data;
  };
  {
  }
  return (
    <>
      {isActive ? (
        <Form
          onFinish={onFinish}
          form={form}
          ref={formRef}
          className="diet-edit-chart-root"
        >
          <DietEditTable
            titleData={() => {
              const dietTotals = combinedTotals;
              const required =
                dietDetails?.nutritional_recommendations ??
                dietDetails?.ia?.nutritional_recommendations;
              const specTags = getLabelTagsWithChecklistLables(
                otherSpecificationsOptions,
                dietDetails?.otherSpecifications ??
                  dietDetails?.ia?.otherSpecifications
              );

              return (
                <div className="diet-edit-chart-header">
                  <div className="diet-edit-chart-header__main">
                    {(dietDetails?.dietType || dietDetails?.ia?.dietType) && (
                      <Typography
                        color="purple"
                        className="diet-edit-chart-diet-type"
                      >
                        Diet Type:{" "}
                        <Typography.Text className="diet-edit-chart-diet-type__value">
                          {dietDetails?.dietType || dietDetails?.ia?.dietType}
                        </Typography.Text>
                      </Typography>
                    )}
                    {specTags ? (
                      <div className="diet-edit-chart-spec-tags">{specTags}</div>
                    ) : null}
                    <div className="diet-edit-chart-tags">
                      {(() => {
                        const combinedTags = [];

                        if (
                          dietTotals?.calories !== undefined &&
                          required?.calories !== undefined
                        ) {
                          combinedTags.push(
                            <Tag
                              key="energy"
                              color="purple"
                              style={{ fontWeight: 600 }}
                            >
                              Energy - {dietTotals?.calories}/
                              {required?.calories} kcal
                            </Tag>
                          );
                        }

                        if (
                          dietTotals?.proteins !== undefined &&
                          required?.protein !== undefined
                        ) {
                          combinedTags.push(
                            <Tag
                              key="proteins"
                              color="purple"
                              style={{ fontWeight: 600 }}
                            >
                              Proteins - {dietTotals?.proteins}/
                              {required?.protein} g
                            </Tag>
                          );
                        }
                        if (
                          dietTotals?.carbohydrates !== undefined &&
                          required?.carbohydrates !== undefined
                        ) {
                          combinedTags.push(
                            <Tag
                              key="carbs"
                              color="purple"
                              style={{ fontWeight: 600 }}
                            >
                              Carbs - {dietTotals?.carbohydrates}/
                              {required?.carbohydrates} g
                            </Tag>
                          );
                        }
                        if (
                          dietTotals?.sodium !== undefined &&
                          required?.sodium !== undefined
                        ) {
                          combinedTags.push(
                            <Tag
                              key="sodium"
                              color="purple"
                              style={{ fontWeight: 600 }}
                            >
                              Sodium - {dietTotals?.sodium}/{required?.sodium} mg
                            </Tag>
                          );
                        }
                        if (
                          dietTotals?.potassium !== undefined &&
                          required?.potassium !== undefined
                        ) {
                          combinedTags.push(
                            <Tag
                              key="potassium"
                              color="purple"
                              style={{ fontWeight: 600 }}
                            >
                              Potassium - {dietTotals?.potassium}/
                              {required?.potassium} mg
                            </Tag>
                          );
                        }
                        return <>{combinedTags}</>;
                      })()}
                    </div>
                    <Form.Item
                      name="comments"
                      label="Comment"
                      className="diet-edit-chart-comment"
                    >
                      <Input.TextArea
                        placeholder="Enter comment"
                        autoSize={{ minRows: 2, maxRows: 4 }}
                        disabled={
                          !(dietDetails?.dietType ?? dietDetails?.ia?.dietType)
                        }
                      />
                    </Form.Item>
                  </div>
                </div>
              );
            }}
            data={mergedData}
            form={form}
            refresh={refresh}
            onChange={handleTableChange}
            dietType={dietDetails?.dietType ?? dietDetails?.ia?.dietType}
            dietDetails={dietDetails}
            combinedTotals={combinedTotals}
          />

          {isActive && (
            <Form.Item className="diet-edit-chart-submit">
              <Button
                type="primary"
                htmlType="submit"
                className={btnStyles.mdBtn}
                disabled={!(dietDetails?.dietType ?? dietDetails?.ia?.dietType)}
              >
                Submit
              </Button>
            </Form.Item>
          )}
        </Form>
      ) : (
        <DietHistoryDrawer open={true} onClose={() => {}} />
      )}
    </>
  );
};

export default NurtritionistDietChart;
